import React from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { Collection } from '../types/collection';

export default function GardenCollections() {
  const [collections, setCollections] = React.useState<Collection[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    async function fetchCollections() {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('collections')
          .select('*')
          .order('name')
          .limit(3);

        if (error) throw error;
        setCollections(data || []);
      } catch (err) {
        console.error('Error fetching collections:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    }

    fetchCollections();
  }, []);

  if (error || collections.length === 0) {
    return null;
  }

  return (
    <div className="py-8 bg-white collections-section">
      <div className="container mx-auto px-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-3">
          <h2 className="text-lg font-bold">Garden Collections</h2>
          <Link 
            to="/collections"
            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
          >
            View All Collections
          </Link>
        </div>
        {loading ? (
          <div className="flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {collections.map((collection) => (
              <Link
                key={collection.id}
                to={`/collections/${collection.slug}`}
                className="group relative overflow-hidden rounded-lg aspect-[16/9] sm:aspect-[4/3]"
              >
                <img
                  src={collection.image_url}
                  alt={collection.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/20" />
                <div className="absolute inset-0 p-3 flex flex-col justify-end">
                  <h3 className="font-medium text-white text-sm mb-1">{collection.name}</h3>
                  <p className="text-gray-200 text-xs line-clamp-2">{collection.description}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}